import { useCallback, useEffect, useRef } from 'react';
import { blockRequiresCompletion, type CourseBlock } from '@course-authoring/shared-types';
import { useAudioControls } from '../AudioControlsContext';
import { pacedBlockCount, type BlockPacing } from '../blockPacing';
import { scrollNarrationBlockBottomIntoView, type NarrationScrollHandle } from '../narrationScroll';
import { attachMeaningfulInteractionListeners } from '../narrationManualIntent';
import type { NarrationAvailability } from './useNarrationScrollCoordinator';

/** Bridges narration to the canvas's single gate owner, including silent intervening blocks. */
export function useNarrationPacing(options: {
  pacing?: BlockPacing;
  blocks: readonly CourseBlock[];
  completed: boolean;
  overlayActive?: boolean;
  root: HTMLElement | null;
  enforceFullVideoView: boolean;
}): void {
  const latest = useRef(options);
  latest.current = options;
  const activeAdvances = useRef(new Set<() => void>());
  const { registerNarrationAvailability } = useAudioControls();
  const prepare = useCallback<NarrationAvailability>((targetId, ready, interrupted) => {
    const pacing = latest.current.pacing;
    if (!pacing || latest.current.completed) {
      ready();
      return () => undefined;
    }
    let cancelled = false;
    let bottomScroll: NarrationScrollHandle | null = null;
    let positionedGeometry: string | null = null;
    let positioning = false;
    let observedBlock: HTMLElement | null = null;
    let resizeObserver: ResizeObserver | null = null;
    let unsubscribe = (): void => undefined;
    let observer: MutationObserver | null = null;
    let detachIntent = (): void => undefined;
    const cancel = (): void => {
      cancelled = true;
      bottomScroll?.cancel();
      unsubscribe();
      activeAdvances.current.delete(advance);
      observer?.disconnect();
      resizeObserver?.disconnect();
      detachIntent();
      document.removeEventListener('visibilitychange', advance);
    };
    const advance = (): void => {
      if (cancelled) return;
      const { blocks, root, overlayActive, completed, enforceFullVideoView } = latest.current;
      if (!root || overlayActive || document.visibilityState === 'hidden') return;
      if (completed) {
        cancel();
        ready();
        return;
      }
      const targetIndex = blocks.findIndex((block) => block.id === targetId);
      if (targetIndex < 0) return;
      const count = completed ? blocks.length : pacedBlockCount(blocks.map((block) => block.id), pacing);
      if (targetIndex < count) {
        cancel();
        ready();
        return;
      }
      // Already accessible content remains usable while a lookup is pending.
      // Hold only narration that would cross the unresolved boundary, without
      // using it as a proxy for bottom visibility.
      if (pacing.getMode() === 'unresolved') return;
      const frontier = blocks[count - 1];
      if (!frontier || pacing.deadline(frontier.id) !== undefined || blockRequiresCompletion(frontier, enforceFullVideoView)) return;
      if (positioning) return;
      // A gate can release before React has committed the newly available block.
      const mounted = Array.from(root.querySelectorAll<HTMLElement>('[data-block-id]'))
        .find((element) => element.dataset.blockId === frontier.id);
      if (!mounted) return;
      if (observedBlock !== mounted) {
        if (observedBlock) resizeObserver?.unobserve(observedBlock);
        observedBlock = mounted;
        resizeObserver?.observe(mounted);
      }
      const anchor = mounted.querySelector('[data-scroll-completion-anchor]') ?? mounted;
      // Use content coordinates: scrolling itself must not trigger another attempt.
      const geometry = JSON.stringify([frontier.id, Math.round(anchor.getBoundingClientRect().bottom + root.scrollTop), root.clientHeight]);
      if (positionedGeometry === geometry) return;
      positionedGeometry = geometry;
      positioning = true;
      let settled = false;
      const handle = scrollNarrationBlockBottomIntoView(frontier.id, () => {
        settled = true;
        positioning = false;
        bottomScroll = null;
        // Visibility, not narration, clears the gate. The shared observer checks the bottom.
        root.dispatchEvent(new Event('scroll'));
        // Media may have grown during the animation. Retry only changed geometry.
        advance();
      }, () => {
        cancel();
        interrupted?.();
      });
      if (!cancelled && !settled) bottomScroll = handle;
      else handle?.cancel();
    };
    activeAdvances.current.add(advance);
    detachIntent = attachMeaningfulInteractionListeners(() => { cancel(); interrupted?.(); });
    unsubscribe = pacing.subscribe(advance);
    resizeObserver = typeof ResizeObserver === 'function' ? new ResizeObserver(advance) : null;
    if (latest.current.root) resizeObserver?.observe(latest.current.root);
    observer = new MutationObserver(advance);
    if (latest.current.root) observer.observe(latest.current.root, { childList: true, subtree: true, attributes: true, attributeFilter: ['data-overlay-active'] });
    document.addEventListener('visibilitychange', advance);
    advance();
    return cancel;
  }, []);
  useEffect(() => registerNarrationAvailability(prepare), [prepare, registerNarrationAvailability, options.pacing]);
  useEffect(() => {
    if (options.completed) activeAdvances.current.forEach((advance) => advance());
  }, [options.completed]);
}
